import React from 'react'
import DataTable from 'react-data-table-component'

/**
 * Shared schedule table component for instructor schedule views.
 * Used in InstructorScheduleList (full page) and ScheduleStatusModal.
 *
 * @param {Object[]} data - Schedule data array
 * @param {Function} onStatusChange - (scheduleId, status) => void
 * @param {boolean} isDarkMode - Dark mode styling
 * @param {boolean} showIdColumn - Show ID column (default: false)
 * @param {boolean} showStudentColumn - Show Student column (default: false)
 * @param {number} paginationPerPage - Rows per page (default: 10)
 * @param {number[]} paginationRowsPerPageOptions - Page size options (default: [10, 25, 50, 100])
 * @param {React.ReactNode} noDataComponent - Message when no data
 */
const InstructorScheduleTable = ({
	data = [],
	onStatusChange,
	isDarkMode = false,
	showIdColumn = false,
	showStudentColumn = false,
	paginationPerPage = 10,
	paginationRowsPerPageOptions = [10, 25, 50, 100],
	noDataComponent = 'No schedule data found'
}) => {
	const getStatusBadgeStyle = (status) => {
		const base = {
			padding: '4px 8px',
			borderRadius: '4px',
			border: '1px solid',
			fontSize: '12px'
		}
		if (status === 'Absent') {
			return {
				...base,
				backgroundColor: isDarkMode ? 'rgba(220, 53, 69, 0.2)' : 'rgba(220, 53, 69, 0.1)',
				color: isDarkMode ? '#ff6b7a' : '#dc3545',
				borderColor: isDarkMode ? 'rgba(220, 53, 69, 0.5)' : 'rgba(220, 53, 69, 0.3)'
			}
		}
		if (status === 'Late') {
			return {
				...base,
				backgroundColor: isDarkMode ? 'rgba(255, 193, 7, 0.2)' : 'rgba(255, 193, 7, 0.1)',
				color: isDarkMode ? '#ffd54f' : '#ffc107',
				borderColor: isDarkMode ? 'rgba(255, 193, 7, 0.5)' : 'rgba(255, 193, 7, 0.3)'
			}
		}
		if (status === 'Present') {
			return {
				...base,
				backgroundColor: isDarkMode ? 'rgba(40, 167, 69, 0.2)' : 'rgba(40, 167, 69, 0.1)',
				color: isDarkMode ? '#4caf50' : '#28a745',
				borderColor: isDarkMode ? 'rgba(40, 167, 69, 0.5)' : 'rgba(40, 167, 69, 0.3)'
			}
		}
		if (status === 'Parent') {
			return {
				...base,
				backgroundColor: isDarkMode ? 'rgba(0, 123, 255, 0.2)' : 'rgba(0, 123, 255, 0.1)',
				color: isDarkMode ? '#64b5f6' : '#007bff',
				borderColor: isDarkMode ? 'rgba(0, 123, 255, 0.5)' : 'rgba(0, 123, 255, 0.3)'
			}
		}
		if (status === 'Made-up') {
			return {
				...base,
				backgroundColor: isDarkMode ? 'rgba(23, 162, 184, 0.2)' : 'rgba(23, 162, 184, 0.1)',
				color: isDarkMode ? '#4dd0e1' : '#17a2b8',
				borderColor: isDarkMode ? 'rgba(23, 162, 184, 0.5)' : 'rgba(23, 162, 184, 0.3)'
			}
		}
		return {
			...base,
			backgroundColor: isDarkMode ? '#2a2d35' : '#fff',
			color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : '#6c757d',
			borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : '#dee2e6'
		}
	}

	const selectStyle = {
		backgroundColor: isDarkMode ? '#2a2d35' : '#fff',
		color: isDarkMode ? '#fff' : 'inherit',
		border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.2)' : '#dee2e6'}`,
		borderRadius: '4px',
		padding: '4px 8px',
		fontSize: '13px',
		minWidth: '120px'
	}

	const columns = React.useMemo(() => {
		const cols = []

		if (showIdColumn) {
			cols.push({
				name: 'ID',
				selector: (row) => row.id,
				sortable: true,
				width: '80px'
			})
		}

		if (showStudentColumn) {
			cols.push({
				name: 'Student',
				selector: (row) => row.studentname,
				sortable: true
			})
		}

		if (!showStudentColumn) {
			cols.push({
				name: 'Class',
				selector: (row) => row.class_name || row.studentname || '-',
				sortable: true,
				style: { color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'inherit' }
			})
		}

		cols.push(
			{
				name: 'Date',
				selector: (row) => row.ldate || '-',
				sortable: true,
				style: { color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'inherit' }
			},
			{
				name: 'Time',
				selector: (row) => (row.ltime ? row.ltime.substring(0, 5) : '-'),
				sortable: true,
				style: { color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'inherit' }
			},
			{
				name: 'Status',
				cell: (row) => (
					<div className="select-container2 mx-2">
						<select
							value={row.status || ''}
							onChange={(e) => onStatusChange(row.id, e.target.value)}
							className={showStudentColumn ? 'form-control' : 'form-control form-control-sm'}
							style={{ ...selectStyle, ...getStatusBadgeStyle(row.status) }}
						>
							<option value="">Select</option>
							<option value="Absent">Absent</option>
							<option value="Late">Late</option>
							<option value="Present">Present</option>
							<option value="Parent">Parent</option>
							<option value="Made-up">Made-up</option>
						</select>
					</div>
				),
				ignoreRowClick: true,
				allowOverflow: true,
				width: showStudentColumn ? '200px' : '140px',
				minWidth: showStudentColumn ? '200px' : '140px'
			}
		)

		return cols
	}, [isDarkMode, showIdColumn, showStudentColumn, onStatusChange])

	const customStyles = {
		headCells: {
			style: {
				fontWeight: 'bold',
				fontSize: showStudentColumn ? '11px' : '12px',
				padding: showStudentColumn ? '12px 8px' : '10px 8px',
				minHeight: showStudentColumn ? '60px' : '48px',
				backgroundColor: isDarkMode ? '#2a2d35' : '#fff',
				color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'inherit',
				borderBottom: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#dee2e6'}`,
				...(showStudentColumn && {
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center'
				})
			}
		},
		cells: {
			style: {
				fontSize: showStudentColumn ? '12px' : '13px',
				padding: showStudentColumn ? '8px' : '12px 8px',
				backgroundColor: isDarkMode ? '#262932' : '#fff',
				color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'inherit'
			}
		},
		rows: {
			style: {
				backgroundColor: isDarkMode ? '#262932' : '#fff',
				'&:nth-of-type(odd)': {
					backgroundColor: isDarkMode ? '#1d1e26' : '#f8f9fa'
				},
				'&:hover': {
					backgroundColor: isDarkMode ? '#2a2d35' : '#e9ecef'
				}
			}
		},
		pagination: {
			style: {
				backgroundColor: isDarkMode ? '#262932' : '#fff',
				color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'inherit'
			}
		}
	}

	return (
		<DataTable
			columns={columns}
			data={data}
			striped
			center={showStudentColumn}
			pagination
			paginationPerPage={paginationPerPage}
			paginationRowsPerPageOptions={paginationRowsPerPageOptions}
			highlightOnHover
			responsive
			customStyles={customStyles}
			theme={isDarkMode ? 'dark' : 'default'}
			noDataComponent={
				typeof noDataComponent === 'string' ? (
					<div className="text-center py-4">{noDataComponent}</div>
				) : (
					noDataComponent
				)
			}
		/>
	)
}

export { InstructorScheduleTable }
